import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { PhCheckCircle } from 'ember-phosphor-icons';

interface SelectableCardSignature {
  Args: {
    description: string;
    isSelected: boolean;
    label: string;
    onChange: (value: string) => void;
    value: string;
    disabled?: boolean;
  };
  Blocks: {
    label: [];
  };
}

const SelectableCard: TOC<SelectableCardSignature> = <template>
  <button
    type="button"
    role="radio"
    aria-checked={{@isSelected}}
    {{on "click" (fn @onChange @value)}}
    class="group flex w-full cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-200 disabled:shadow-none
      {{if @isSelected 'border-violet-600 ring-2 ring-violet-600' 'border-gray-300'}}"
    data-test={{@value}}
    disabled={{@disabled}}
  >
    <div class="flex-1 text-left">
      <span class="tio-text-sm-medium">
        {{#if (has-block "label")}}
          {{yield to="label"}}
        {{else}}
          {{@label}}
        {{/if}}
      </span>
      <span class="mt-2 text-sm tio-fg-muted">{{@description}}</span>
    </div>
    {{#if @isSelected}}
      <span class="text-violet-600 fill-current ml-2">
        <PhCheckCircle @size="24" role="presentation" aria-hidden="true" />
      </span>
    {{/if}}
  </button>
</template>;

export default SelectableCard;

/**
 * Selectable Card Component
 *
 * A simple, accessible component that behaves as a clickable card for single-selection options.
 * It visually mimics a radio button group but is rendered as a button element with enhanced styling.
 *
 * ## Notes:
 *
 * - The component is intended for single-selection (radio button–like) use cases. For multi-selection, consider a different pattern.
 * - Ensure that the `eq` helper (used to compare `selectedAction` with each card's `@value`) is available and functioning in your project.
 *
 * /**
 * SelectableCard Component
 *
 * A simple, accessible component that behaves as a clickable card for single-selection options.
 *
 * @param {Object} args
 * @param {string} args.value - A unique identifier for the card. This value is passed back to the parent when the card is clicked.
 * @param {string} args.label - The primary text displayed on the card, used as the title.
 * @param {string} args.description - A short explanation or subtitle displayed under the label.
 * @param {boolean} args.isSelected - Indicates whether the card is currently selected. When true, the card displays with an indigo border, ring, and a check mark icon.
 * @param {(value: string) => void} args.onChange - A callback function that gets called when the card is clicked. It receives the card’s `value` as its argument.
 *
 * @example
 * <SelectableCard
 *   @value="settle"
 *   @label="Settle"
 *   @description="Last message sent an hour ago"
 *   @isSelected={{eq this.selectedAction "settle"}}
 *   @onChange={{this.setAction}}
 * />
 *
 * <div class="tio-flex-loose">
 *   <SelectableCard
 *     @value="settle"
 *     @label="Settle"
 *     @description="Last message sent an hour ago"
 *     @isSelected={{eq this.selectedAction "settle"}}
 *     @onChange={{this.setAction}}
 *   />
 *   <SelectableCard
 *     @value="forgive"
 *     @label="Forgive"
 *     @description="You have been forgiven"
 *     @isSelected={{eq this.selectedAction "forgive"}}
 *     @onChange={{this.setAction}}
 *   />
 * </div>
 */
